import type { TranscriptSnippet } from "@expert/shared-types";
import type { JSX } from "react";

import { Flex, Text } from "@mantine/core";
import { Virtuoso } from "react-virtuoso";

import { useVerifiedCustomerStore } from "@/customer-info";
import { useSessionId } from "@/sdk";
import { TranscriptFooter } from "@/transcript/TranscriptFooter";
import { TranscriptLoading } from "@/transcript/TranscriptLoading";
import { getTranscriptTimestamp } from "@/transcript/utils/getTranscriptTimestamp";

export function TranscriptBody({
    snippets,
    taskStartTimeInMs,
    hasSentiment,
}: {
    taskStartTimeInMs: number;
    snippets?: TranscriptSnippet[];
    hasSentiment: boolean;
}): JSX.Element | undefined {
    const sessionId = useSessionId();
    const firstName = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer?.firstName);

    return (
        <Virtuoso
            initialTopMostItemIndex={snippets ? snippets.length - 1 : 0}
            atBottomThreshold={60}
            components={{ Footer: !snippets ? TranscriptLoading : undefined }}
            followOutput={(isAtBottom) => (isAtBottom ? "smooth" : false)}
            style={{
                height: `calc(100vh - ${hasSentiment ? "11.5rem" : "9.5rem"})`,
                padding: "1rem",
                width: "100%",
                overflowX: "hidden",
                scrollbarColor: "var(--mantine-color-gray-7) var(--mantine-color-dark-5)",
            }}
            data={snippets}
            itemContent={(index, snippet) => (
                <>
                    <Flex
                        style={{ transition: "all 500ms", width: "85%" }}
                        pb="lg"
                        direction="column"
                        key={snippet.content.concat(snippet.timestamp.toString())}
                    >
                        <Flex w="100%" justify="space-between">
                            <Text c={snippet.speaker === "Expert" ? "dark.2" : "primary.2"} size="xs">
                                {snippet.speaker === "Expert" ? "You" : (firstName ?? "Customer")}
                            </Text>
                            <Text c="dark.2" size="xs">
                                {getTranscriptTimestamp(taskStartTimeInMs, snippet.timestamp)}
                            </Text>
                        </Flex>
                        <Text style={{ wordBreak: "break-word" }} pt="3xs" c="dark.1" size="xs">
                            {snippet.content}
                        </Text>
                    </Flex>
                    {index === (snippets?.length ?? 1) - 1 && <TranscriptFooter />}
                </>
            )}
        ></Virtuoso>
    );
}
